import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { assetsAPI } from '../utils/api';
import { toast } from 'sonner';

export function EditAssetModal({ open, onClose, onUpdated, asset }) {
  const [form, setForm] = useState({
    asset_name: '', installed_power_kw: '', installed_capacity_kwh: '',
    manufacturer: '', model: '', serial_number: '', vendor_asset_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (asset) {
      setForm({
        asset_name: asset.asset_name || '',
        installed_power_kw: asset.installed_power_kw ?? '',
        installed_capacity_kwh: asset.installed_capacity_kwh ?? '',
        manufacturer: asset.manufacturer || '',
        model: asset.model || '',
        serial_number: asset.serial_number || '',
        vendor_asset_id: asset.vendor_asset_id || '',
      });
    }
  }, [asset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.asset_name || form.installed_power_kw === '' || form.installed_capacity_kwh === '') {
      toast.error('Name, Power, and Capacity are required');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('asset_name', form.asset_name);
      formData.append('installed_power_kw', form.installed_power_kw);
      formData.append('installed_capacity_kwh', form.installed_capacity_kwh);
      if (form.manufacturer) formData.append('manufacturer', form.manufacturer);
      if (form.model) formData.append('model', form.model);
      if (form.serial_number) formData.append('serial_number', form.serial_number);
      if (form.vendor_asset_id) formData.append('vendor_asset_id', form.vendor_asset_id);
      const res = await assetsAPI.update(asset.asset_id, formData);
      toast.success('Asset updated');
      onUpdated?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="edit-asset-modal">
        <DialogHeader>
          <DialogTitle>Edit BESS Asset</DialogTitle>
          <DialogDescription>Update power, capacity and identification details for this asset.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-3">
          <div>
            <Label>Asset Name *</Label>
            <Input value={form.asset_name} onChange={e => setForm({ ...form, asset_name: e.target.value })} className="mt-1" data-testid="edit-asset-name-input" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Power (kW) *</Label>
              <Input type="number" step="any" value={form.installed_power_kw} onChange={e => setForm({ ...form, installed_power_kw: e.target.value })} className="mt-1" data-testid="edit-asset-power-input" required />
            </div>
            <div>
              <Label>Capacity (kWh) *</Label>
              <Input type="number" step="any" value={form.installed_capacity_kwh} onChange={e => setForm({ ...form, installed_capacity_kwh: e.target.value })} className="mt-1" data-testid="edit-asset-capacity-input" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Manufacturer</Label>
              <Input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} className="mt-1" data-testid="edit-asset-mfr-input" />
            </div>
            <div>
              <Label>Model</Label>
              <Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} className="mt-1" data-testid="edit-asset-model-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Serial Number</Label>
              <Input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} className="mt-1" data-testid="edit-asset-serial-input" />
            </div>
            <div>
              <Label>Vendor Asset ID</Label>
              <Input value={form.vendor_asset_id} onChange={e => setForm({ ...form, vendor_asset_id: e.target.value })} className="mt-1" data-testid="edit-asset-vendorid-input" />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-3 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-asset-submit">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
